import { observable, configure, action } from 'mobx';
import util from 'preact-util';
import LocalModel from './localModel';

configure({ enforceActions: 'always' });

// Mirror of NSF competitions (backend lib/nsf-competition-sync-run.js): the
// terminliste list, map and detail. Read-only for users.
const DEFAULT_FILTERS = {
    from: '',
    to: '',
    branchName: '',
    disciplineGroup: '',
    kretsName: '',
    distrikt: '',
    search: '',
    championship: '',
    includeCancelled: 1,
    lat: '',
    lng: '',
    maxDistanceKm: '',
};

const LIST_LIMIT = 2000;

class NsfCompetitionStore extends LocalModel {
    constructor() {
        super('nsfCompetition', {
            namePlural: 'nsfCompetitions',
            namePluralReal: 'nsfcompetitions',
            sort: 'startDate',
            limit: LIST_LIMIT,
            api: {
                search: {
                    url: '/api/nsfcompetitions/',
                    params: {
                        limit: LIST_LIMIT,
                        sort: 'startDate',
                    },
                },
                load: {
                    url: '/api/nsfcompetitions/',
                    params: {},
                },
                save: {
                    url: '/api/nsfcompetitions/',
                    params: {},
                },
                delete: {
                    url: '/api/nsfcompetitions/',
                    params: {},
                },
            },
        });
    }

    @observable nsfCompetitions = [];

    @observable nsfCompetition = {};

    @observable filterOptions = {
        branchNames: [], disciplineGroups: [], kretsNames: [], distrikter: [], lastSyncRun: null,
    };

    @observable filters = { ...DEFAULT_FILTERS };

    @observable loading = false;

    @observable total = 0;

    /** Rows for the stevneplanlegger: its own window, never the list's filters. */
    @observable plannerRows = [];

    @observable plannerLoading = false;

    @action
    setFilter(key, value) {
        this.updateObjectKeyValue('filters', key, value);
    }

    @action
    setFilters(values = {}) {
        this.updateKeyValue('filters', { ...DEFAULT_FILTERS, ...values });
    }

    @action
    resetFilters() {
        this.updateKeyValue('filters', { ...DEFAULT_FILTERS });
    }

    /** Query params for the list: only the non-empty filters plus the fixed limit. */
    listParams() {
        const params = {};
        Object.keys(this.filters).forEach((key) => {
            const value = this.filters[key];
            if (value !== '' && value !== null && value !== undefined) {
                params[key] = value;
            }
        });
        if (params.includeCancelled === undefined) {
            params.includeCancelled = 1;
        }
        params.limit = LIST_LIMIT;
        return params;
    }

    @action
    async loadFilters() {
        try {
            const response = await util.fetchApi('/api/nsfcompetitions/filters', { method: 'GET' }, {});
            if (response?.status === 200 && response.data) {
                this.updateKeyValue('filterOptions', {
                    branchNames: response.data.branchNames || [],
                    disciplineGroups: response.data.disciplineGroups || [],
                    kretsNames: response.data.kretsNames || [],
                    distrikter: response.data.distrikter || [],
                    lastSyncRun: response.data.lastSyncRun || null,
                });
            }
        } catch (err) {
            console.error('nsfCompetition loadFilters failed', err);
        }
    }

    @action
    async loadList() {
        this.updateKeyValue('loading', true);
        try {
            const response = await util.fetchApi('/api/nsfcompetitions/', { method: 'GET' }, this.listParams());
            if (response?.status === 200) {
                this.updateKeyValue('nsfCompetitions', response.data || []);
                this.updateKeyValue('total', response.total || (response.data || []).length);
            }
        } catch (err) {
            console.error('nsfCompetition loadList failed', err);
        } finally {
            this.updateKeyValue('loading', false);
        }
    }

    @action
    async loadPlannerRows(params = {}) {
        this.updateKeyValue('plannerLoading', true);
        try {
            const response = await util.fetchApi('/api/nsfcompetitions/', { method: 'GET' }, {
                ...params, includeCancelled: 0, limit: LIST_LIMIT,
            });
            if (response?.status === 200) {
                this.updateKeyValue('plannerRows', response.data || []);
            }
        } catch (err) {
            console.error('nsfCompetition loadPlannerRows failed', err);
        } finally {
            this.updateKeyValue('plannerLoading', false);
        }
    }

    @observable convention = null;

    /** «Resultater»: the convention on this site reported under the stevne's stevnenummer, or null. */
    @action
    async loadConvention(id) {
        this.updateKeyValue('convention', null);
        try {
            const response = await util.fetchApi(`/api/nsfcompetitions/${id}/convention`, { method: 'GET' }, {});
            if (response?.status === 200) {
                this.updateKeyValue('convention', response.data || null);
            }
        } catch (err) {
            console.error('nsfCompetition loadConvention failed', err);
        }
    }

    @action
    async loadOne(id) {
        try {
            const response = await util.fetchApi(`/api/nsfcompetitions/${id}`, { method: 'GET' }, {});
            if (response?.status === 200 && Array.isArray(response.data) && response.data[0]) {
                this.updateKeyValue('nsfCompetition', response.data[0]);
                return response.data[0];
            }
            this.updateKeyValue('nsfCompetition', {});
        } catch (err) {
            console.error('nsfCompetition loadOne failed', err);
            this.updateKeyValue('nsfCompetition', {});
        }
        return null;
    }
}

const store = new NsfCompetitionStore();
export { NsfCompetitionStore, DEFAULT_FILTERS };
export default store;
